/**
 * Pet Profile Drawer Component
 * 
 * Displays pet profile information in a side drawer
 */

if (!customElements.get('pet-profile-drawer')) {
  customElements.define(
    'pet-profile-drawer',
    class PetProfileDrawer extends DrawerComponent {
      constructor() {
        super();
        this.currentPet = null;
        this.changes = {};
        this.originalPetData = null;
        this.editingFieldKey = null;
        this.fileInputClicking = false; // Flag to prevent double-triggering
        this.saving = false; // Flag to prevent multiple simultaneous saves
        this.deleting = false;
        
        // Store bound methods for proper event listener removal
        this.boundHandleClick = this.handleClick.bind(this);
        this.boundHandleFieldChange = this.handleFieldChange.bind(this);
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.boundHandleImageUpload = this.handleImageUpload.bind(this);
        this.boundDeleteModalClick = this.handleDeleteModalClick.bind(this);
        /** @type {HTMLElement | null} */
        this.deleteConfirmModalEl = null;
      }

      get requiresBodyAppended() {
        return true;
      }

      prepareToShow() {
        super.prepareToShow();
        // Pet data should be set before showing
      }

      show(petData, triggerElement) {
        if (!petData) {
          console.error('No pet data provided to drawer');
          return;
        }

        this.currentPet = petData;
        this.originalPetData = JSON.parse(JSON.stringify(petData)); // Deep copy
        this.changes = {};
        this.editingFieldKey = null;
        this.updateDrawerHeader(petData);
        this.renderPetData(petData);
        this.updateFooterVisibility();
        super.show(triggerElement);
      }

      updateDrawerHeader(pet) {
        const headerTitle = this.querySelector('.drawer__heading span');
        if (headerTitle) {
          const petName = pet.petName || 'Pet Profile';
          headerTitle.textContent = `${petName}'s Profile`;
        }
      }

      renderPetData(pet) {
        const drawerBody = this.querySelector('.pet-profile-drawer__body');
        if (!drawerBody) {
          console.error('Drawer body not found');
          return;
        }

        // Show content container
        const contentContainer = drawerBody.querySelector('.pet-profile-drawer__content');
        if (!contentContainer) {
          console.error('Content container not found');
          return;
        }
        contentContainer.hidden = false;
        
        // Normalize petEatsTreats from boolean to string if needed
        if (pet.petEatsTreats !== undefined && typeof pet.petEatsTreats === 'boolean') {
          pet.petEatsTreats = pet.petEatsTreats ? 'yes' : 'no';
        }

        // Check if petProfileImage exists and has a url
        const hasImage = pet.petProfileImage && pet.petProfileImage.url && pet.petProfileImage.url.trim() !== '';
        const noPetImageUrl = this.dataset.noPetImage || '/assets/no-pet-image.jpg';

        // Get current image URL (might be changed but not saved)
        const currentImageUrl = this.changes.petProfileImagePreview || (hasImage ? pet.petProfileImage.url : '');
        const displayImageUrl = currentImageUrl || (hasImage ? pet.petProfileImage.url : '') || noPetImageUrl;

        // Update image
        const petImage = contentContainer.querySelector('[data-pet-image]');
        if (petImage) {
          petImage.src = displayImageUrl;
          petImage.alt = this.escapeHtml(pet.petName || 'Pet');
        }

        // Update image edit button - hide in quiz mode
        const imageEditBtn = contentContainer.querySelector('[data-edit-image]');
        const imageEditText = contentContainer.querySelector('[data-image-edit-text]');
        const fileInput = contentContainer.querySelector('[data-image-upload]');
        const imageContainer = contentContainer.querySelector('.pet-profile-drawer__image-container');
        const isQuizMode = this.dataset.quizMode === 'true';
        
        if (isQuizMode) {
          // Hide entire image container in quiz mode
          if (imageContainer) {
            imageContainer.hidden = true;
            imageContainer.style.display = 'none';
          }
          if (imageEditBtn) {
            imageEditBtn.hidden = true;
            imageEditBtn.style.display = 'none';
          }
          if (imageEditText) {
            imageEditText.hidden = true;
            imageEditText.style.display = 'none';
          }
          if (fileInput) {
            fileInput.disabled = true;
            fileInput.style.display = 'none';
          }
        } else {
          // Show image container and edit functionality in regular mode
          if (imageContainer) {
            imageContainer.hidden = false;
            imageContainer.style.display = '';
          }
          if (imageEditBtn) {
            imageEditBtn.hidden = false;
            imageEditBtn.style.display = '';
            imageEditBtn.setAttribute('aria-label', displayImageUrl === noPetImageUrl ? 'Add pet image' : 'Replace pet image');
          }
          if (imageEditText) {
            imageEditText.hidden = false;
            imageEditText.style.display = '';
            imageEditText.textContent = displayImageUrl === noPetImageUrl ? 'Add Image' : 'Replace Image';
          }
          if (fileInput) {
            fileInput.disabled = false;
            fileInput.style.display = '';
          }
        }

        // Update pet name
        const petNameEl = contentContainer.querySelector('[data-pet-name]');
        if (petNameEl) {
          petNameEl.textContent = pet.petName || '';
        }

        // Update pet type
        const petTypeEl = contentContainer.querySelector('[data-pet-type]');
        const petTypeTextEl = contentContainer.querySelector('[data-pet-type-text]');
        if (pet.petType) {
          const petTypeFormatted = pet.petType.charAt(0).toUpperCase() + pet.petType.slice(1);
          if (petTypeEl) {
            petTypeEl.hidden = false;
          }
          if (petTypeTextEl) {
            petTypeTextEl.textContent = petTypeFormatted;
          }
        } else {
          if (petTypeEl) {
            petTypeEl.hidden = true;
          }
        }

        // Update pet breed
        const petBreedEl = contentContainer.querySelector('[data-pet-breed]');
        if (petBreedEl) {
          if (pet.petBreed) {
            petBreedEl.textContent = this.escapeHtml(pet.petBreed);
            petBreedEl.hidden = false;
          } else {
            petBreedEl.hidden = true;
          }
        }

        const deletePetBtn = contentContainer.querySelector('[data-delete-pet]');
        if (deletePetBtn) {
          if (isQuizMode) {
            deletePetBtn.hidden = true;
            deletePetBtn.style.display = 'none';
          } else {
            deletePetBtn.hidden = false;
            deletePetBtn.style.display = '';
          }
        }

        // Update pet age
        const petAgeEl = contentContainer.querySelector('[data-pet-age]');
        const petAge = this.calculatePetAge(pet.petBirthday);
        if (petAgeEl) {
          if (petAge) {
            petAgeEl.textContent = `${petAge} old`;
            petAgeEl.hidden = false;
          } else {
            petAgeEl.hidden = true;
          }
        }

        // Update gender icon
        const genderIconContainer = contentContainer.querySelector('[data-pet-gender-display]');
        if (genderIconContainer) {
          const petGender = pet.petGender || '';
          if (petGender) {
            const genderLower = petGender.toLowerCase();
            if (genderLower === 'male' || genderLower === 'female') {
              genderIconContainer.hidden = false;
              this.loadGenderIcon(genderIconContainer, genderLower);
            } else {
              genderIconContainer.hidden = true;
            }
          } else {
            genderIconContainer.hidden = true;
          }
        }

        // Build and update table rows
        const tableBody = contentContainer.querySelector('[data-table-body]');
        if (tableBody) {
          const tableRows = this.buildTableRows(pet);
          if (tableRows) {
            tableBody.innerHTML = tableRows;
            // Load section icons after HTML is inserted
            this.loadSectionIcons();
          } else {
            tableBody.innerHTML = '<tr><td colspan="2">No data available</td></tr>';
          }
        }
      }

      async loadSectionIcons() {
        const sectionHeaders = this.querySelectorAll('.pet-profile-drawer__section-header');
        for (const header of sectionHeaders) {
          const sectionKey = header.dataset.sectionKey;
          const iconContainer = header.querySelector('[data-section-icon]');
          if (!iconContainer || !sectionKey) continue;

          const iconUrl = this.dataset[`icon${sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)}`];
          if (!iconUrl) continue;

          try {
            const response = await fetch(iconUrl);
            if (!response.ok) continue;

            const svgContent = await response.text();
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');
            
            if (svgElement) {
              // Remove fixed width/height to allow proper scaling
              svgElement.removeAttribute('width');
              svgElement.removeAttribute('height');
              // Ensure it scales properly
              svgElement.setAttribute('width', '100%');
              svgElement.setAttribute('height', '100%');
              svgElement.setAttribute('aria-hidden', 'true');
              
              // Preserve original fill and stroke colors from SVG
              // Use inline styles with !important to override any CSS
              const paths = svgElement.querySelectorAll('path');
              paths.forEach(path => {
                // Preserve fill if it exists (use !important to override CSS)
                const fillValue = path.getAttribute('fill');
                if (fillValue && fillValue !== 'none') {
                  path.style.setProperty('fill', fillValue, 'important');
                }
                // Preserve stroke if it exists (use !important to override CSS)
                const strokeValue = path.getAttribute('stroke');
                if (strokeValue) {
                  path.style.setProperty('stroke', strokeValue, 'important');
                }
              });
              
              iconContainer.innerHTML = '';
              iconContainer.appendChild(svgElement);
            }
          } catch (error) {
            console.error(`Error loading section icon for ${sectionKey}:`, error);
          }
        }
      }

      buildTableRows(pet) {
        const rows = [];
        
        if (!pet || typeof pet !== 'object') {
          console.error('Invalid pet data:', pet);
          return '';
        }

        // Helper function to format field labels
        const formatLabel = (key) => {
          // Custom labels for specific fields
          const customLabels = {
            'petBreed': 'Breed',
            'petBrandFood': 'Food Brand',
            'petDietChangeReason': 'Change Reason',
            'petName': 'Name',
            'petBirthday': 'Birthday',
            'petGender': 'Gender',
            'petWeight': 'Weight',
            'petActivityLevel': 'Activity Level',
            'petBodyType': 'Body Type',
            'petFoodType': 'Food Type',
            'petAllergies': 'Allergies',
            'petPooType': 'Poo Type',
            'isPetRescue': 'Rescue Pet',
            'petEatsTreats': 'Eats Treats',
            'petPronoun': 'Pronoun'
          };
          
          if (customLabels[key]) {
            return customLabels[key];
          }
          
          return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
        };

        // Helper function to format values
        const formatValue = (value, key) => {
          if (value === null || value === undefined) {
            return '<span class="text-subtext">Not set</span>';
          }

          // Handle arrays
          if (Array.isArray(value)) {
            // Special handling for allergies - validate array contents
            if (key === 'petAllergies') {
              // Filter out invalid values and ensure all items are strings
              const validAllergies = value
                .filter(item => item !== null && item !== undefined && String(item).trim() !== '')
                .map(item => String(item).trim());
              
              if (validAllergies.length === 0 || (validAllergies.length === 1 && validAllergies[0].toLowerCase() === 'none')) {
                return '<span class="text-subtext">None</span>';
              }
              
              // Filter out "none" if other allergies exist
              const filteredAllergies = validAllergies.filter(a => a.toLowerCase() !== 'none');
              if (filteredAllergies.length === 0) {
                return '<span class="text-subtext">None</span>';
              }
              
              return `<div class="pet-profile-drawer__allergy-rows">
                ${filteredAllergies.map(item => `
                  <div class="pet-profile-drawer__allergy-row">
                    <span>${this.escapeHtml(String(item))}</span>
                  </div>
                `).join('')}
              </div>`;
            }
            
            // Special handling for petDietChangeReason
            if (key === 'petDietChangeReason') {
              const validReasons = value
                .filter(item => item !== null && item !== undefined && String(item).trim() !== '')
                .map(item => String(item).trim());
              
              if (validReasons.length === 0) {
                return '<span class="text-subtext">Not set</span>';
              }
              
              return `<ul class="pet-profile-drawer__list">
                ${validReasons.map(item => `<li>${this.escapeHtml(String(item))}</li>`).join('')}
              </ul>`;
            }
            
            if (value.length === 0) {
              return '<span class="text-subtext">None</span>';
            }
            return `<ul class="pet-profile-drawer__list">
              ${value.map(item => `<li>${this.escapeHtml(String(item))}</li>`).join('')}
            </ul>`;
          }
          
          // If petAllergies is not an array, treat it as invalid and show "None"
          if (key === 'petAllergies') {
            console.warn('petAllergies is not an array, normalizing to empty array');
            return '<span class="text-subtext">None</span>';
          }
          
          // If petDietChangeReason is not an array, treat it as invalid
          if (key === 'petDietChangeReason') {
            if (value && typeof value === 'string' && value.trim() !== '') {
              // If it's a string, convert to array
              return `<ul class="pet-profile-drawer__list">
                <li>${this.escapeHtml(value)}</li>
              </ul>`;
            }
            return '<span class="text-subtext">Not set</span>';
          }

          // Handle booleans
          if (typeof value === 'boolean') {
            return value ? '<span class="text-success">Yes</span>' : '<span class="text-subtext">No</span>';
          }

          // Handle dates
          if (key.includes('Date') || key.includes('At') || key === 'petBirthday') {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                // For birthday, show date only
                if (key === 'petBirthday') {
                  return date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric'
                  });
                }
                // For timestamps (createdAt, updatedAt), show date and time
                return date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              }
            } catch (e) {
              // Fall through to default formatting
            }
          }

          // Handle numbers
          if (typeof value === 'number') {
            // Add units for weight
            if (key === 'petWeight') {
              return `${value} kg`;
            }
            // Format ratio fields as percentages
            if (key === 'dryFoodRatio' || key === 'wetFoodRatio') {
              return `${Math.round(value)}%`;
            }
            return String(value);
          }

          // Handle strings
          if (typeof value === 'string') {
            // Capitalize pet type
            if (key === 'petType') {
              return this.escapeHtml(value.charAt(0).toUpperCase() + value.slice(1));
            }
            // Format pet food type with ratios when mix
            if (key === 'petFoodType') {
              const foodType = value.toLowerCase().trim();
              let formatted = this.escapeHtml(value.charAt(0).toUpperCase() + value.slice(1));
              if (foodType === 'mix') {
                const dryRatio = pet.dryFoodRatio;
                const wetRatio = pet.wetFoodRatio;
                if (dryRatio !== null && dryRatio !== undefined && wetRatio !== null && wetRatio !== undefined) {
                  formatted += ` (${Math.round(dryRatio)}/${Math.round(wetRatio)})`;
                }
              }
              return formatted;
            }
            // Look up metaobject labels for petPooType, petActivityLevel, petBodyType, petEatsTreats
            if (key === 'petPooType' || key === 'petActivityLevel' || key === 'petBodyType' || key === 'petEatsTreats') {
              const metaobjectLabel = this.getMetaobjectLabel(key, value);
              if (metaobjectLabel) {
                return this.escapeHtml(metaobjectLabel);
              }
              // Fallback to capitalized value if metaobject lookup fails
              return this.escapeHtml(value.split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' '));
            }
            return this.escapeHtml(value);
          }
          
          // Handle boolean values (but not petEatsTreats which should be string)
          if (typeof value === 'boolean' && key !== 'petEatsTreats') {
            return value ? '<span class="text-success">Yes</span>' : '<span class="text-subtext">No</span>';
          }

          return this.escapeHtml(String(value));
        };

        // Check if drawer is in quiz mode
        const isQuizMode = this.dataset.quizMode === 'true';
        
        // Define sections with their fields
        // Quiz-only fields: petBreed, petBrandFood, petDietChangeReason
        const generalFields = ['petName', 'petBirthday', 'petGender', 'petPooType', 'isPetRescue', 'petPronoun'];
        if (isQuizMode) {
          // Add petBreed only in quiz mode
          generalFields.splice(3, 0, 'petBreed');
        }
        
        const sections = {
          general: {
            title: 'General',
            iconUrl: this.dataset.iconGeneral,
            fields: generalFields
          },
          activity: {
            title: 'Activity',
            iconUrl: this.dataset.iconActivity,
            fields: ['petWeight', 'petBodyType', 'petActivityLevel']
          },
          diet: {
            title: 'Diet',
            iconUrl: this.dataset.iconDiet,
            fields: ['petFoodType', 'wetFoodRatio', 'dryFoodRatio', 'petBrandFood', 'petEatsTreats', 'petAllergies', 'petDietChangeReason'],
            // Conditionally include ratio fields based on petFoodType and quiz-only fields
            getFields: (pet) => {
              const baseFields = ['petFoodType', 'petEatsTreats', 'petAllergies'];
              
              // Add quiz-only fields only in quiz mode
              if (isQuizMode) {
                baseFields.splice(1, 0, 'petBrandFood');
                baseFields.push('petDietChangeReason');
              }
              
              const petFoodType = pet?.petFoodType?.toLowerCase()?.trim();
              if (petFoodType === 'mix') {
                // Insert ratio fields after petFoodType
                const insertIndex = isQuizMode ? 2 : 1; // After petFoodType (and petBrandFood if quiz mode)
                baseFields.splice(insertIndex, 0, 'dryFoodRatio', 'wetFoodRatio');
              }
              return baseFields;
            }
          }
        };

        // Build rows for each section
        Object.entries(sections).forEach(([sectionKey, section]) => {
          // Add section header with icon placeholder
          rows.push(`
            <tr class="pet-profile-drawer__section-header" data-section-key="${sectionKey}">
              <th colspan="2">
                <span class="pet-profile-drawer__section-header-content">
                  <span class="pet-profile-drawer__section-icon" data-section-icon="${sectionKey}"></span>
                  <span class="pet-profile-drawer__section-title">${this.escapeHtml(section.title)}</span>
                </span>
              </th>
            </tr>
          `);

          // Build rows for each field in this section
          const fieldsToRender = section.getFields ? section.getFields(pet) : section.fields;
          fieldsToRender.forEach(key => {
            // Show field if it exists in pet OR if it's a ratio field when food type is mix
            // Always show fields that are defined in the section (they may not exist in pet data yet)
            const petFoodType = pet?.petFoodType?.toLowerCase()?.trim();
            const isRatioField = key === 'dryFoodRatio' || key === 'wetFoodRatio';
            // Always show fields in the section, or if it's a ratio field when food type is mix
            const shouldShowField = true; // Always show fields defined in section
            
            if (shouldShowField) {
              const label = formatLabel(key);
              // Support petDateOfBirth when petBirthday not set (API/dataset compatibility)
              const fieldValue = (pet[key] !== undefined ? pet[key] : null) ?? (key === 'petBirthday' ? (pet.petDateOfBirth ?? null) : null);
              const value = formatValue(fieldValue, key);
              const originalValue = fieldValue;
              
              rows.push(`
                <tr class="pet-profile-drawer__row" data-field-key="${this.escapeHtml(key)}" data-field-type="${this.getFieldType(key, originalValue)}">
                  <th>
                    <div class="pet-profile-drawer__field-header">
                      <button 
                        class="pet-profile-drawer__edit-btn" 
                        type="button"
                        aria-label="Edit ${this.escapeHtml(label)}"
                        data-field-key="${this.escapeHtml(key)}"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="icon icon-pencil icon--small">
                          <path d="M5.79313 13.5001H3C2.86739 13.5001 2.74021 13.4474 2.64645 13.3536C2.55268 13.2599 2.5 13.1327 2.5 13.0001V10.207C2.50006 10.0745 2.55266 9.94753 2.64625 9.85383L10.3538 2.14633C10.4475 2.05263 10.5746 2 10.7072 2C10.8397 2 10.9669 2.05263 11.0606 2.14633L13.8538 4.93758C13.9474 5.03134 14.0001 5.15847 14.0001 5.29102C14.0001 5.42357 13.9474 5.5507 13.8538 5.64446L6.14625 13.3538C6.05255 13.4474 5.92556 13.5 5.79313 13.5001Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M8.5 4L12 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                      <span>${this.escapeHtml(label)}</span>
                    </div>
                  </th>
                  <td class="pet-profile-drawer__field-value" data-field-key="${this.escapeHtml(key)}" style="text-transform: capitalize;">
                    ${value}
                  </td>
                </tr>
              `);
            }
          });
        });

        const result = rows.join('');
        return result;
      }

      getFieldType(key, value) {
        if (Array.isArray(value)) return 'array';
        if (typeof value === 'boolean' && key !== 'petEatsTreats') return 'boolean';
        if (key.includes('Date') || key.includes('At') || key === 'petBirthday') return 'date';
        // Recognize ratio fields by name (even if value is null/undefined)
        if (key === 'dryFoodRatio' || key === 'wetFoodRatio' || key === 'petWeight') return 'number';
        if (typeof value === 'number') return 'number';
        if (key === 'petType') return 'select';
        if (key === 'petBreed') return 'select';
        if (key === 'petGender') return 'select';
        if (key === 'petFoodType') return 'select';
        if (key === 'petActivityLevel') return 'select';
        if (key === 'petBodyType') return 'select';
        if (key === 'petPooType') return 'select';
        if (key === 'petEatsTreats') return 'select';
        if (key === 'petDietChangeReason') return 'array';
        if (key === 'petBrandFood') return 'text';
        return 'text';
      }

      connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.boundHandleClick);
        this.addEventListener('change', this.boundHandleFieldChange);
        this.addEventListener('input', this.boundHandleFieldChange);
        this.addEventListener('keydown', this.boundHandleKeyDown);
        
        // Add real-time date validation on blur
        this.addEventListener('blur', (event) => {
          const field = event.target;
          if (field && field.type === 'date' && field.hasAttribute('data-field-input') && field.getAttribute('data-field-input') === 'petBirthday') {
            this.validateDateField(field);
          }
        }, true); // Use capture phase to catch blur events
        
        // Use event delegation for file input changes, but only for data-image-upload
        this.addEventListener('change', (e) => {
          if (e.target && e.target.hasAttribute('data-image-upload')) {
            this.handleImageUpload(e);
          }
        });
        // Prevent file input from triggering click events that bubble up
        this.addEventListener('click', (e) => {
          if (e.target && e.target.hasAttribute('data-image-upload')) {
            e.stopPropagation();
          }
        }, true); // Use capture phase to catch early

        // Delete confirmation lives in basic-modal, which is moved to document.body when open.
        // Clicks there never bubble to this element, so delegate from the modal itself.
        this.deleteConfirmModalEl = this.querySelector('[data-pet-delete-modal]');
        if (this.deleteConfirmModalEl) {
          this.deleteConfirmModalEl.addEventListener('click', this.boundDeleteModalClick);
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.boundHandleClick);
        this.removeEventListener('change', this.boundHandleFieldChange);
        this.removeEventListener('input', this.boundHandleFieldChange);
        this.removeEventListener('keydown', this.boundHandleKeyDown);
        if (this.deleteConfirmModalEl) {
          this.deleteConfirmModalEl.removeEventListener('click', this.boundDeleteModalClick);
          this.deleteConfirmModalEl = null;
        }
      }

      handleDeleteModalClick(e) {
        const cancelBtn = e.target.closest('[data-delete-pet-cancel]');
        if (cancelBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.closeDeleteConfirmModal();
          return;
        }

        const confirmBtn = e.target.closest('[data-delete-pet-confirm]');
        if (confirmBtn) {
          e.preventDefault();
          e.stopPropagation();
          if (!this.deleting) {
            this.confirmDeletePet();
          }
        }
      }

      handleKeyDown(e) {
        if (e.key === 'Escape' && this.editingFieldKey) {
          e.preventDefault();
          this.finishEditingField(this.editingFieldKey, false);
        }
      }

      handleClick(e) {
        // Allergy tag button - handle first to prevent other handlers from interfering
        const allergyTag = e.target.closest('.pet-profile-drawer__allergy-tag');
        if (allergyTag) {
          const tagCloud = allergyTag.closest('.pet-profile-drawer__allergy-tag-cloud');
          if (tagCloud && this.editingFieldKey === 'petAllergies') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.toggleAllergyTag(allergyTag);
            return;
          }
        }

        const editBtn = e.target.closest('.pet-profile-drawer__edit-btn');
        if (editBtn && editBtn.dataset.fieldKey) {
          e.preventDefault();
          e.stopPropagation();
          const fieldKey = editBtn.dataset.fieldKey;
          this.startEditingField(fieldKey);
          return;
        }

        const saveBtn = e.target.closest('[data-save-all]');
        if (saveBtn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          // Prevent multiple simultaneous saves
          if (!this.saving) {
            this.saveAllChanges();
          }
          return;
        }

        const cancelBtn = e.target.closest('[data-cancel-all]');
        if (cancelBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.cancelAllChanges();
          return;
        }

        const deletePetTrigger = e.target.closest('[data-delete-pet]');
        if (deletePetTrigger) {
          if (this.dataset.quizMode === 'true') {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          this.openDeleteConfirmModal();
          return;
        }

        // Click outside - finish editing current field
        if (this.editingFieldKey) {
          const clickedInput = e.target.closest('[data-field-input]');
          const clickedTagCloud = e.target.closest('.pet-profile-drawer__allergy-tag-cloud');
          if (!clickedInput && !clickedTagCloud) {
            this.finishEditingField(this.editingFieldKey, true);
          }
        }

        // Image edit button - disabled in quiz mode
        const imageEditBtn = e.target.closest('[data-edit-image]');
        if (imageEditBtn) {
          // Don't allow image editing in quiz mode
          if (this.dataset.quizMode === 'true') {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          // Prevent double-triggering: don't click if already clicking or if file input was clicked
          if (this.fileInputClicking || (e.target && e.target.hasAttribute('data-image-upload'))) {
            return;
          }
          const fileInput = this.querySelector('[data-image-upload]');
          if (fileInput && fileInput !== e.target) {
            this.fileInputClicking = true;
            fileInput.click();
            // Reset flag after a short delay
            setTimeout(() => {
              this.fileInputClicking = false;
            }, 300);
          }
          return;
        }

      }

      toggleAllergyTag(tagButton) {
        // Prevent double-clicks
        if (tagButton.dataset.processing === 'true') {
          return;
        }
        tagButton.dataset.processing = 'true';
        setTimeout(() => {
          tagButton.dataset.processing = 'false';
        }, 300);
        
        const tagCloud = tagButton.closest('.pet-profile-drawer__allergy-tag-cloud');
        if (!tagCloud) {
          return;
        }

        const allergyValue = tagButton.dataset.allergyValue;
        const hiddenInput = tagCloud.querySelector('[data-allergy-input]');
        if (!hiddenInput) {
          return;
        }

        // Get current selected allergies
        let selectedAllergies = [];
        try {
          const currentValue = hiddenInput.value;
          if (currentValue) {
            selectedAllergies = JSON.parse(currentValue);
          }
        } catch (e) {
          console.error('Error parsing allergy values:', e);
        }

        // Toggle the allergy
        const normalizedValue = allergyValue.toLowerCase().trim();
        const index = selectedAllergies.findIndex(a => a.toLowerCase().trim() === normalizedValue);
        
        if (index > -1) {
          // Remove if already selected
          selectedAllergies.splice(index, 1);
          tagButton.classList.remove('pet-profile-drawer__allergy-tag--selected');
          tagButton.setAttribute('aria-pressed', 'false');
        } else {
          // Add if not selected
          // Remove "none" if selecting something else
          if (normalizedValue !== 'none') {
            selectedAllergies = selectedAllergies.filter(a => a.toLowerCase().trim() !== 'none');
          } else {
            // If selecting "none", clear all others
            selectedAllergies = [];
          }
          selectedAllergies.push(normalizedValue);
          tagButton.classList.add('pet-profile-drawer__allergy-tag--selected');
          tagButton.setAttribute('aria-pressed', 'true');
        }

        // If "none" is selected, deselect all others
        if (normalizedValue === 'none' && tagButton.classList.contains('pet-profile-drawer__allergy-tag--selected')) {
          const allTags = tagCloud.querySelectorAll('.pet-profile-drawer__allergy-tag');
          allTags.forEach(tag => {
            if (tag !== tagButton && tag.classList.contains('pet-profile-drawer__allergy-tag--selected')) {
              tag.classList.remove('pet-profile-drawer__allergy-tag--selected');
              tag.setAttribute('aria-pressed', 'false');
            }
          });
          selectedAllergies = ['none'];
        } else if (normalizedValue !== 'none') {
          // If selecting something other than "none", deselect "none"
          const noneTag = tagCloud.querySelector('[data-allergy-value="none"]');
          if (noneTag && noneTag.classList.contains('pet-profile-drawer__allergy-tag--selected')) {
            noneTag.classList.remove('pet-profile-drawer__allergy-tag--selected');
            noneTag.setAttribute('aria-pressed', 'false');
            selectedAllergies = selectedAllergies.filter(a => a.toLowerCase().trim() !== 'none');
          }
        }

        // Update hidden input
        hiddenInput.value = JSON.stringify(selectedAllergies);

        // Trigger change event to update changes tracking
        const event = new Event('change', { bubbles: true });
        hiddenInput.dispatchEvent(event);
      }

      async compressImage(file, maxWidth = 600, maxHeight = 600, quality = 0.85) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              // Calculate new dimensions maintaining aspect ratio
              let width = img.width;
              let height = img.height;

              if (width > maxWidth || height > maxHeight) {
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width = width * ratio;
                height = height * ratio;
              }

              // Create canvas and compress
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);

              // Convert to blob with compression
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    // Convert blob to base64
                    const reader2 = new FileReader();
                    reader2.onload = () => {
                      resolve({
                        base64: reader2.result.split(',')[1],
                        dataUrl: reader2.result,
                        fileName: file.name,
                        originalSize: file.size,
                        compressedSize: blob.size
                      });
                    };
                    reader2.onerror = reject;
                    reader2.readAsDataURL(blob);
                  } else {
                    reject(new Error('Failed to compress image'));
                  }
                },
                file.type || 'image/jpeg',
                quality
              );
            };
            img.onerror = reject;
            img.src = e.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      async handleImageUpload(e) {
        // Don't allow image upload in quiz mode
        if (this.dataset.quizMode === 'true') {
          return;
        }
        
        // Stop event propagation to prevent double triggers
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const fileInput = e.target;
        // Ensure we're handling the file input directly, not a parent element
        if (!fileInput || !fileInput.hasAttribute('data-image-upload') || !fileInput.files || fileInput.files.length === 0) {
          return;
        }

        const file = fileInput.files[0];
        if (!file.type.startsWith('image/')) {
          alert('Please select an image file');
          return;
        }

        try {
          // Show loading state
          const petImage = this.querySelector('[data-pet-image]');
          if (petImage) {
            petImage.style.opacity = '0.5';
            petImage.alt = 'Compressing image...';
          }

          // Compress image
          const compressed = await this.compressImage(file);
          
          // Update the image display
          if (petImage) {
            petImage.src = compressed.dataUrl;
            petImage.alt = this.escapeHtml(this.currentPet.petName || 'Pet');
            petImage.style.opacity = '1';
          }

          // Update image edit button text
          const imageEditText = this.querySelector('[data-image-edit-text]');
          if (imageEditText) {
            imageEditText.textContent = 'Replace Image';
          }

          // Store compressed file data for upload using StoredFileInput format
          this.changes.petProfileImage = {
            fileName: compressed.fileName,
            base64: compressed.base64
          };
          
          // Store preview URL for display
          this.changes.petProfileImagePreview = compressed.dataUrl;
          
          // Log compression stats (optional, can be removed)
          if (compressed.originalSize && compressed.compressedSize) {
            const savings = ((1 - compressed.compressedSize / compressed.originalSize) * 100).toFixed(1);
            console.log(`Image compressed: ${(compressed.originalSize / 1024).toFixed(1)}KB → ${(compressed.compressedSize / 1024).toFixed(1)}KB (${savings}% reduction)`);
          }
          
          this.updateFooterVisibility();
          this.updateSaveButton();
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Failed to process image. Please try again.');
        }
      }

      handleFieldChange(e) {
        // Handle allergy tag cloud hidden input
        const allergyInput = e.target.hasAttribute && e.target.hasAttribute('data-allergy-input') 
          ? e.target 
          : e.target.closest('[data-allergy-input]');
        if (allergyInput && allergyInput.dataset.allergyInput === 'petAllergies') {
          const fieldKey = 'petAllergies';
          const fieldType = 'array';
          let value = this.parseFieldValue(allergyInput.value, fieldType, fieldKey);
          
          // Compare with original value
          const originalValue = this.originalPetData[fieldKey] || [];
          const normalizedOriginal = Array.isArray(originalValue) 
            ? originalValue.map(v => String(v).toLowerCase().trim()).sort()
            : [];
          const normalizedValue = Array.isArray(value)
            ? value.map(v => String(v).toLowerCase().trim()).sort()
            : [];
          
          if (JSON.stringify(normalizedValue) !== JSON.stringify(normalizedOriginal)) {
            this.changes[fieldKey] = value;
          } else {
            delete this.changes[fieldKey];
          }

          this.updateFooterVisibility();
          this.updateSaveButton();
          return;
        }

        const input = e.target.closest('[data-field-input]');
        if (!input) return;

        const fieldKey = input.dataset.fieldInput;
        if (!fieldKey) return;

        // Track changes
        const fieldType = input.closest('tr')?.dataset.fieldType;
        let value = this.parseFieldValue(input.value, fieldType, fieldKey);
        
        // Validate date of birth field
        if (fieldKey === 'petBirthday' && fieldType === 'date' && input.type === 'date') {
          const dateValue = input.value.trim();
          if (dateValue) {
            const selectedDate = new Date(dateValue);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Calculate minimum date: max(1996-01-01, 30 years ago)
            const currentYear = today.getFullYear();
            const thirtyYearsAgo = currentYear - 30;
            const minYear = Math.max(1996, thirtyYearsAgo);
            const minDate = new Date(minYear, 0, 1);
            
            // Remove existing error
            const row = input.closest('tr');
            const existingError = row?.querySelector('.pet-profile-drawer__date-error');
            if (existingError) {
              existingError.remove();
            }
            input.classList.remove('error');
            input.removeAttribute('aria-invalid');
            
            let isValid = true;
            let errorMessage = '';
            
            // Check if date is valid
            if (isNaN(selectedDate.getTime())) {
              isValid = false;
              errorMessage = 'Please enter a valid date';
            } 
            // Check if date is in the future
            else if (selectedDate > today) {
              isValid = false;
              errorMessage = 'Date cannot be in the future';
            } 
            // Check if date is older than 30 years (or before 1996)
            else if (selectedDate < minDate) {
              isValid = false;
              errorMessage = `Date must be after ${minYear}-01-01 and not more than 30 years ago`;
            }
            
            if (!isValid) {
              input.classList.add('error');
              input.setAttribute('aria-invalid', 'true');
              
              // Show error message
              const errorDiv = document.createElement('div');
              errorDiv.className = 'pet-profile-drawer__date-error pet-quiz-slide__error-message';
              errorDiv.textContent = errorMessage;
              
              if (row) {
                const valueCell = row.querySelector('.pet-profile-drawer__field-value');
                if (valueCell) {
                  valueCell.appendChild(errorDiv);
                } else {
                  row.appendChild(errorDiv);
                }
              }
              
              // Don't track invalid date as a change
              return;
            }
          }
        }
        
        // Special handling for petFoodType change - show/hide ratio fields immediately
        if (fieldKey === 'petFoodType') {
          const newFoodType = String(value).toLowerCase().trim();
          const oldFoodType = String(this.currentPet.petFoodType || '').toLowerCase().trim();
          
          // Update currentPet immediately so table rebuild shows correct fields
          this.currentPet[fieldKey] = value;
          
          if (newFoodType === 'mix') {
            // Changing to mix - ensure ratios exist (default to 50/50 if not set or null)
            const currentDry = this.currentPet.dryFoodRatio;
            const currentWet = this.currentPet.wetFoodRatio;
            
            // If ratios are null/undefined (backend cleared them), use defaults
            if (currentDry === null || currentDry === undefined) {
              this.currentPet.dryFoodRatio = 50;
              this.changes.dryFoodRatio = 50;
            }
            if (currentWet === null || currentWet === undefined) {
              this.currentPet.wetFoodRatio = 50;
              this.changes.wetFoodRatio = 50;
            }
            
            // Ensure ratios sum to 100
            const dryValue = this.currentPet.dryFoodRatio;
            const wetValue = this.currentPet.wetFoodRatio;
            const total = dryValue + wetValue;
            if (total !== 100) {
              this.currentPet.wetFoodRatio = 100 - dryValue;
              this.changes.wetFoodRatio = this.currentPet.wetFoodRatio;
            }
            
            // Immediately rebuild table to show ratio fields
            const tableBody = this.querySelector('[data-table-body]');
            if (tableBody) {
              const allRows = this.buildTableRows(this.currentPet);
              if (allRows) {
                tableBody.innerHTML = allRows;
                this.loadSectionIcons();
              }
            }
          } else if (oldFoodType === 'mix') {
            // Changing from mix to dry/wet - clear ratios
            delete this.changes.dryFoodRatio;
            delete this.changes.wetFoodRatio;
            this.currentPet.dryFoodRatio = null;
            this.currentPet.wetFoodRatio = null;
            
            // Immediately rebuild table to hide ratio fields
            const tableBody = this.querySelector('[data-table-body]');
            if (tableBody) {
              const allRows = this.buildTableRows(this.currentPet);
              if (allRows) {
                tableBody.innerHTML = allRows;
                this.loadSectionIcons();
              }
            }
          }
        }
        
        // Special handling for ratio fields - auto-calculate complementary ratio
        if (fieldKey === 'dryFoodRatio' || fieldKey === 'wetFoodRatio') {
          const complementaryKey = fieldKey === 'dryFoodRatio' ? 'wetFoodRatio' : 'dryFoodRatio';
          const complementaryValue = Math.max(0, Math.min(100, 100 - value));
          
          // Update the complementary ratio in currentPet and changes
          this.currentPet[complementaryKey] = complementaryValue;
          
          // Update the complementary field's input if it's currently being edited
          if (this.editingFieldKey === complementaryKey) {
            const complementaryRow = this.querySelector(`tr[data-field-key="${complementaryKey}"]`);
            const complementaryValueCell = complementaryRow?.querySelector('.pet-profile-drawer__field-value');
            const complementaryInput = complementaryValueCell?.querySelector('input');
            if (complementaryInput) {
              complementaryInput.value = complementaryValue;
            }
          }
          
          // Track complementary ratio change if it differs from original
          const complementaryOriginal = this.originalPetData[complementaryKey];
          if (complementaryValue !== complementaryOriginal) {
            this.changes[complementaryKey] = complementaryValue;
          } else {
            delete this.changes[complementaryKey];
          }
        }
        
        // Compare with original value
        const originalValue = this.originalPetData[fieldKey];
        if (JSON.stringify(value) !== JSON.stringify(originalValue)) {
          this.changes[fieldKey] = value;
        } else {
          delete this.changes[fieldKey];
        }

        this.updateFooterVisibility();
        this.updateSaveButton();
      }

      startEditingField(fieldKey) {
        // If already editing this field, do nothing
        if (this.editingFieldKey === fieldKey) return;

        // If editing another field, save it first
        if (this.editingFieldKey && this.editingFieldKey !== fieldKey) {
          this.finishEditingField(this.editingFieldKey, false);
        }

        const row = this.querySelector(`tr[data-field-key="${fieldKey}"]`);
        if (!row) return;

        const valueCell = row.querySelector('.pet-profile-drawer__field-value');
        if (!valueCell) return;

        const fieldType = row.dataset.fieldType;
        const currentValue = this.currentPet[fieldKey];

        // Store original HTML
        valueCell.dataset.originalHtml = valueCell.innerHTML;
        
        // Create editable input
        const inputHTML = this.createEditInput(fieldKey, fieldType, currentValue);
        valueCell.innerHTML = inputHTML;

        // Focus the input
        const input = valueCell.querySelector('input, select, textarea');
        if (input) {
          setTimeout(() => input.focus(), 100);
        }
        
        // Initialize brand search if this is petBrandFood in quiz mode
        if (fieldKey === 'petBrandFood' && this.dataset.quizMode === 'true' && this.brandLists) {
          setTimeout(() => {
            this.initBrandSearchInDrawer(valueCell, fieldKey);
          }, 100);
        }

        this.editingFieldKey = fieldKey;
        this.updateFooterVisibility();
      }
      
      initBrandSearchInDrawer(container, fieldKey) {
        const brandInput = container.querySelector('[data-brand-search-input]');
        const dropdown = container.querySelector('.pet-quiz-slide__breed-dropdown');
        const hiddenInput = container.querySelector('[data-brand-value-input]');
        
        if (!brandInput || !dropdown || !hiddenInput) return;
        
        // Get brands for current pet type
        const petType = this.currentPet?.petType || '';
        let brands = [];
        if (petType) {
          const normalizedType = petType.toLowerCase().trim();
          if (normalizedType === 'dog' && this.brandLists.dog) {
            brands = this.brandLists.dog;
          } else if (normalizedType === 'cat' && this.brandLists.cat) {
            brands = this.brandLists.cat;
          } else {
            brands = [...(this.brandLists.dog || []), ...(this.brandLists.cat || [])];
          }
        } else {
          brands = [...(this.brandLists.dog || []), ...(this.brandLists.cat || [])];
        }
        
        // Remove duplicates and sort
        brands = [...new Set(brands)].sort();
        
        let filteredBrands = brands;
        let selectedIndex = -1;
        
        const renderDropdown = () => {
          dropdown.innerHTML = '';
          
          if (filteredBrands.length === 0) {
            const noResults = document.createElement('div');
            noResults.className = 'pet-quiz-slide__breed-dropdown-item pet-quiz-slide__breed-dropdown-item--no-results';
            noResults.textContent = 'No brands found';
            dropdown.appendChild(noResults);
            dropdown.style.display = 'block';
            return;
          }
          
          filteredBrands.forEach((brand, index) => {
            const item = document.createElement('div');
            item.className = 'pet-quiz-slide__breed-dropdown-item';
            if (index === selectedIndex) {
              item.classList.add('pet-quiz-slide__breed-dropdown-item--selected');
            }
            item.textContent = brand.trim();
            item.setAttribute('role', 'option');
            item.setAttribute('data-brand-value', brand.trim());
            
            item.addEventListener('click', () => {
              const brandValue = item.getAttribute('data-brand-value');
              brandInput.value = item.textContent;
              hiddenInput.value = brandValue;
              dropdown.style.display = 'none';
              
              // Trigger change event to update changes tracking
              const event = new Event('change', { bubbles: true });
              brandInput.dispatchEvent(event);
            });
            
            dropdown.appendChild(item);
          });
          
          dropdown.style.display = filteredBrands.length > 0 ? 'block' : 'none';
        };
        
        // Show dropdown on click
        brandInput.addEventListener('click', () => {
          if (filteredBrands.length > 0) {
            renderDropdown();
          }
        });
        
        // Filter brands on input
        brandInput.addEventListener('input', (e) => {
          const query = e.target.value.toLowerCase().trim();
          selectedIndex = -1;
          
          // Update hidden input with current text value (user might type custom value)
          hiddenInput.value = e.target.value.trim();
          
          if (query === '') {
            filteredBrands = brands;
          } else {
            filteredBrands = brands.filter(brand => {
              return brand.toLowerCase().includes(query);
            });
          }
          
          renderDropdown();
          
          // Trigger change event to update changes tracking
          const event = new Event('change', { bubbles: true });
          brandInput.dispatchEvent(event);
        });
        
        // Handle keyboard navigation
        brandInput.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            selectedIndex = Math.min(selectedIndex + 1, filteredBrands.length - 1);
            renderDropdown();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest' });
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            selectedIndex = Math.max(selectedIndex - 1, -1);
            renderDropdown();
            if (selectedIndex >= 0) {
              const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
              if (selectedItem) {
                selectedItem.scrollIntoView({ block: 'nearest' });
              }
            }
          } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.click();
            }
          } else if (e.key === 'Escape') {
            dropdown.style.display = 'none';
          }
        });
        
        // Close dropdown when clicking outside
        const handleClickOutside = (e) => {
          if (!container.contains(e.target)) {
            dropdown.style.display = 'none';
          }
        };
        document.addEventListener('click', handleClickOutside);
        
        // Store cleanup
        brandInput._brandSearchCleanup = () => {
          document.removeEventListener('click', handleClickOutside);
        };
      }

      finishEditingField(fieldKey, save = true) {
        if (!this.editingFieldKey || this.editingFieldKey !== fieldKey) return;

        const row = this.querySelector(`tr[data-field-key="${fieldKey}"]`);
        if (!row) return;

        const valueCell = row.querySelector('.pet-profile-drawer__field-value');
        if (!valueCell) return;

        if (save) {
          // Handle allergy tag cloud specially
          if (fieldKey === 'petAllergies') {
            const tagCloud = valueCell.querySelector('.pet-profile-drawer__allergy-tag-cloud');
            const hiddenInput = tagCloud?.querySelector('[data-allergy-input]');
            if (hiddenInput) {
              const fieldType = row.dataset.fieldType;
              let newValue;
              
              try {
                // Parse the hidden input value
                newValue = this.parseFieldValue(hiddenInput.value, fieldType, fieldKey);
                
                // Validate that we got a valid array
                if (!Array.isArray(newValue)) {
                  // If parsing failed or returned invalid data, use original value
                  console.warn('Invalid allergy data, using original value');
                  newValue = this.originalPetData[fieldKey] || [];
                }
              } catch (e) {
                // If parsing completely fails, use original value
                console.error('Error parsing allergy data:', e);
                newValue = this.originalPetData[fieldKey] || [];
              }
              
              // Update changes tracking
              const originalValue = this.originalPetData[fieldKey] || [];
              const normalizedOriginal = Array.isArray(originalValue) 
                ? originalValue.map(v => String(v).toLowerCase().trim()).sort()
                : [];
              const normalizedValue = Array.isArray(newValue)
                ? newValue.map(v => String(v).toLowerCase().trim()).sort()
                : [];
              
              if (JSON.stringify(normalizedValue) !== JSON.stringify(normalizedOriginal)) {
                this.changes[fieldKey] = newValue;
              } else {
                delete this.changes[fieldKey];
              }

              // Update current pet data for display
              this.currentPet[fieldKey] = newValue;
            } else {
              // If tag cloud doesn't exist, use original value
              this.currentPet[fieldKey] = this.originalPetData[fieldKey] || [];
            }
          } else {
            // Get value from input
            let input = valueCell.querySelector('input, select, textarea');
            let inputValue = input ? input.value : '';
            
            // Special handling for petBrandFood in quiz mode - get value from hidden input
            if (fieldKey === 'petBrandFood' && this.dataset.quizMode === 'true') {
              const hiddenInput = valueCell.querySelector('[data-brand-value-input]');
              if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
                inputValue = hiddenInput.value;
              } else if (input && input.value && input.value.trim() !== '') {
                inputValue = input.value;
              }
            }
            
            if (input) {
              const fieldType = row.dataset.fieldType;
              const newValue = this.parseFieldValue(inputValue, fieldType, fieldKey);
              
              // Special handling for petType change - update breed options
              if (fieldKey === 'petType' && newValue !== this.currentPet.petType) {
                // Clear breed selection when pet type changes
                if (this.currentPet.petBreed) {
                  delete this.changes.petBreed;
                  this.currentPet.petBreed = null;
                }
              }
              
              // Special handling for petFoodType change - show/hide ratio fields
              if (fieldKey === 'petFoodType' && newValue !== this.currentPet.petFoodType) {
                const newFoodType = String(newValue).toLowerCase().trim();
                const oldFoodType = String(this.currentPet.petFoodType || '').toLowerCase().trim();
                
                if (newFoodType === 'mix') {
                  // Changing to mix - ensure ratios exist (default to 50/50 if not set or null)
                  // If backend cleared ratios (null), use defaults. Otherwise use existing values.
                  const currentDry = this.currentPet.dryFoodRatio;
                  const currentWet = this.currentPet.wetFoodRatio;
                  
                  // If ratios are null/undefined (backend cleared them), use defaults
                  if (currentDry === null || currentDry === undefined) {
                    this.currentPet.dryFoodRatio = 50;
                    this.changes.dryFoodRatio = 50;
                  }
                  if (currentWet === null || currentWet === undefined) {
                    this.currentPet.wetFoodRatio = 50;
                    this.changes.wetFoodRatio = 50;
                  }
                  
                  // Ensure ratios sum to 100 (in case one was set but not the other)
                  const dryValue = this.currentPet.dryFoodRatio;
                  const wetValue = this.currentPet.wetFoodRatio;
                  const total = dryValue + wetValue;
                  if (total !== 100) {
                    // Adjust to sum to 100, prioritizing dry ratio
                    this.currentPet.wetFoodRatio = 100 - dryValue;
                    this.changes.wetFoodRatio = this.currentPet.wetFoodRatio;
                  }
                } else if (oldFoodType === 'mix') {
                  // Changing from mix to dry/wet - clear ratios (backend will also clear them)
                  delete this.changes.dryFoodRatio;
                  delete this.changes.wetFoodRatio;
                  this.currentPet.dryFoodRatio = null;
                  this.currentPet.wetFoodRatio = null;
                }
              }
              
              // Special handling for ratio fields - auto-calculate complementary ratio
              if (fieldKey === 'dryFoodRatio' || fieldKey === 'wetFoodRatio') {
                const complementaryKey = fieldKey === 'dryFoodRatio' ? 'wetFoodRatio' : 'dryFoodRatio';
                const complementaryValue = Math.max(0, Math.min(100, 100 - newValue));
                
                // Update the complementary ratio in currentPet
                this.currentPet[complementaryKey] = complementaryValue;
                
                // Track complementary ratio change if it differs from original
                const complementaryOriginal = this.originalPetData[complementaryKey];
                if (complementaryValue !== complementaryOriginal) {
                  this.changes[complementaryKey] = complementaryValue;
                } else {
                  delete this.changes[complementaryKey];
                }
                
                // Update the complementary field's display if it's not being edited
                if (this.editingFieldKey !== complementaryKey) {
                  const complementaryRow = this.querySelector(`tr[data-field-key="${complementaryKey}"]`);
                  if (complementaryRow) {
                    const complementaryValueCell = complementaryRow.querySelector('.pet-profile-drawer__field-value');
                    if (complementaryValueCell) {
                      const displayValue = this.formatValue(complementaryValue, complementaryKey);
                      complementaryValueCell.innerHTML = displayValue;
                    }
                  }
                }
              }
              
              // Update changes tracking
              const originalValue = this.originalPetData[fieldKey];
              if (JSON.stringify(newValue) !== JSON.stringify(originalValue)) {
                this.changes[fieldKey] = newValue;
              } else {
                delete this.changes[fieldKey];
              }

              // Update current pet data for display
              this.currentPet[fieldKey] = newValue;
              
              // If pet type changed, re-render breed field with new options
              if (fieldKey === 'petType' && newValue !== this.originalPetData.petType) {
                const breedRow = this.querySelector(`tr[data-field-key="petBreed"]`);
                if (breedRow) {
                  const breedValueCell = breedRow.querySelector('.pet-profile-drawer__field-value');
                  if (breedValueCell) {
                    const breedDisplayValue = this.formatValue(this.currentPet.petBreed, 'petBreed');
                    breedValueCell.innerHTML = breedDisplayValue;
                  }
                }
              }
              
              // If pet food type changed, re-render the entire diet section to show/hide ratio fields
              if (fieldKey === 'petFoodType' && newValue !== this.originalPetData.petFoodType) {
                // Find the diet section table body
                const tableBody = this.querySelector('[data-table-body]');
                if (tableBody) {
                  // Rebuild all rows to include/exclude ratio fields
                  const allRows = this.buildTableRows(this.currentPet);
                  if (allRows) {
                    tableBody.innerHTML = allRows;
                    // Reload section icons after HTML is inserted
                    this.loadSectionIcons();
                  }
                }
              }
            }
          }
        }

        // Cleanup brand search if it was initialized
        if (fieldKey === 'petBrandFood') {
          const brandInput = valueCell.querySelector('[data-brand-search-input]');
          if (brandInput && brandInput._brandSearchCleanup) {
            brandInput._brandSearchCleanup();
            delete brandInput._brandSearchCleanup;
          }
        }
        
        // Restore display mode
        const originalHtml = valueCell.dataset.originalHtml;
        if (originalHtml) {
          // Re-render with current value
          const fieldType = row.dataset.fieldType;
          const displayValue = this.formatValue(this.currentPet[fieldKey], fieldKey);
          valueCell.innerHTML = displayValue;
        }

        this.editingFieldKey = null;
        this.updateFooterVisibility();
      }


      createEditInput(fieldKey, fieldType, currentValue) {
        if (fieldType === 'boolean') {
          return `
            <div class="select">
              <select class="form-control form-control--select" data-field-input="${fieldKey}">
                <option value="true" ${currentValue === true ? 'selected' : ''}>Yes</option>
                <option value="false" ${currentValue === false ? 'selected' : ''}>No</option>
              </select>
              ${this.getCaretIcon()}
            </div>
          `;
        } else if (fieldType === 'date') {
          const dateValue = currentValue ? new Date(currentValue).toISOString().split('T')[0] : '';
          // Calculate minimum date: max(1996-01-01, 30 years ago)
          const today = new Date();
          const currentYear = today.getFullYear();
          const thirtyYearsAgo = currentYear - 30;
          const minYear = Math.max(1996, thirtyYearsAgo);
          const minDate = `${minYear}-01-01`;
          const maxDate = today.toISOString().split('T')[0];
          
          // Add min/max attributes for petBirthday field
          const minMaxAttrs = fieldKey === 'petBirthday' 
            ? `min="${minDate}" max="${maxDate}"`
            : '';
          
          return `<input type="date" class="form-control" value="${this.escapeHtml(dateValue)}" data-field-input="${fieldKey}" ${minMaxAttrs}>`;
        } else if (fieldType === 'number') {
          return `<input type="number" class="form-control" value="${this.escapeHtml(currentValue || '')}" data-field-input="${fieldKey}" step="${fieldKey === 'petWeight' ? '0.1' : '1'}">`;
        } else if (fieldType === 'select') {
          const options = this.getSelectOptions(fieldKey);
          // Normalize currentValue for petEatsTreats (convert boolean to string)
          let normalizedValue = currentValue;
          if (fieldKey === 'petEatsTreats' && typeof currentValue === 'boolean') {
            normalizedValue = currentValue ? 'yes' : 'no';
          }
          return `
            <div class="select">
              <select class="form-control form-control--select" data-field-input="${fieldKey}">
                ${options.map(opt => `<option value="${this.escapeHtml(opt.value)}" ${opt.value === normalizedValue ? 'selected' : ''}>${this.escapeHtml(opt.label)}</option>`).join('')}
              </select>
              ${this.getCaretIcon()}
            </div>
          `;
        } else if (fieldType === 'array' && fieldKey === 'petAllergies') {
          // Special handling for allergies - use tag cloud
          // Ensure currentValue is a valid array
          let validAllergies = [];
          if (Array.isArray(currentValue)) {
            validAllergies = currentValue.filter(item => item !== null && item !== undefined);
          } else if (currentValue !== null && currentValue !== undefined) {
            // Try to normalize invalid values
            try {
              if (typeof currentValue === 'string') {
                // If it's a string, try to parse it
                const parsed = JSON.parse(currentValue);
                if (Array.isArray(parsed)) {
                  validAllergies = parsed;
                }
              }
            } catch (e) {
              // If parsing fails, use empty array
              validAllergies = [];
            }
          }
          return this.createAllergyTagCloud(validAllergies);
        } else if (fieldType === 'array') {
          const arrayValue = Array.isArray(currentValue) ? currentValue.join(', ') : '';
          return `<textarea class="form-control form-control--textarea" data-field-input="${fieldKey}" rows="3" placeholder="Enter values separated by commas">${this.escapeHtml(arrayValue)}</textarea>`;
        } else if (fieldKey === 'petBrandFood' && this.dataset.quizMode === 'true' && this.brandLists) {
          // Special handling for petBrandFood in quiz mode - create searchable dropdown
          const uniqueId = `pet-brand-drawer-${Date.now()}`;
          return `
            <div class="pet-quiz-slide__breed-search-container">
              <input
                type="text"
                class="form-control pet-quiz-slide__breed-search-input"
                value="${this.escapeHtml(currentValue || '')}"
                data-field-input="${fieldKey}"
                data-brand-search-input
                id="${uniqueId}-input"
                placeholder="Search food brand"
                autocomplete="off"
              >
              <input
                type="hidden"
                data-brand-value-input
                id="${uniqueId}-value"
                value="${this.escapeHtml(currentValue || '')}"
              >
              <div
                class="pet-quiz-slide__breed-dropdown"
                id="${uniqueId}-dropdown"
                role="listbox"
                aria-label="Brand options"
              ></div>
            </div>
          `;
        } else {
          return `<input type="text" class="form-control" value="${this.escapeHtml(currentValue || '')}" data-field-input="${fieldKey}">`;
        }
      }

      parseFieldValue(value, fieldType, fieldKey) {
        // Handle petEatsTreats as string (select field), not boolean
        if (fieldKey === 'petEatsTreats') {
          return String(value || '').trim();
        }
        if (fieldType === 'boolean') {
          return value === 'true';
        } else if (fieldType === 'number' || fieldKey === 'dryFoodRatio' || fieldKey === 'wetFoodRatio' || fieldKey === 'petWeight') {
          // Ensure ratio fields and weight are always parsed as numbers
          const numValue = parseFloat(value);
          return isNaN(numValue) ? 0 : numValue;
        } else if (fieldType === 'array') {
          if (fieldKey === 'petAllergies') {
            // Handle allergy tag cloud - value is JSON string
            if (!value || value.trim() === '' || value.trim() === '[]' || value.trim() === '[') {
              return [];
            }
            try {
              const parsed = JSON.parse(value);
              if (Array.isArray(parsed)) {
                return parsed.filter(item => item && String(item).trim() !== '');
              }
            } catch (e) {
              // If JSON parsing fails, return empty array for allergies
              console.warn('Failed to parse allergy JSON, returning empty array:', e);
              return [];
            }
            // If parsing succeeded but didn't return array, return empty array
            return [];
          }
          // Handle petDietChangeReason and other array fields - comma-separated values
          if (fieldKey === 'petDietChangeReason') {
            if (!value || value.trim() === '') {
              return [];
            }
            // Split by comma and filter out empty values
            return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
          }
          return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        } else if (fieldType === 'date') {
          return value ? new Date(value).toISOString() : null;
        }
        return value;
      }

      updateFooterVisibility() {
        const footer = this.querySelector('.pet-profile-drawer__footer');
        if (footer) {
          const hasChanges = Object.keys(this.changes).length > 0;
          const isEditing = this.editingFieldKey !== null;
          
          if (hasChanges || isEditing) {
            footer.classList.remove('hidden');
          } else {
            footer.classList.add('hidden');
          }
        }
      }

      updateSaveButton() {
        const saveBtn = this.querySelector('[data-save-all]');
        if (saveBtn) {
          const hasChanges = Object.keys(this.changes).length > 0;
          
          // Validate: if petFoodType is "mix", ensure ratios are present
          const petFoodType = this.changes.petFoodType !== undefined 
            ? this.changes.petFoodType 
            : this.currentPet.petFoodType;
          const foodType = String(petFoodType || '').toLowerCase().trim();
          
          let isValid = hasChanges;
          if (foodType === 'mix') {
            // Check if ratios exist in changes or currentPet
            const hasDryRatio = (this.changes.dryFoodRatio !== undefined) || 
                               (this.currentPet.dryFoodRatio !== null && this.currentPet.dryFoodRatio !== undefined);
            const hasWetRatio = (this.changes.wetFoodRatio !== undefined) || 
                               (this.currentPet.wetFoodRatio !== null && this.currentPet.wetFoodRatio !== undefined);
            isValid = hasChanges && hasDryRatio && hasWetRatio;
          }
          
          saveBtn.disabled = !isValid;
        }
      }

      async saveAllChanges() {
        // Prevent multiple simultaneous saves
        if (this.saving) {
          return;
        }
        
        if (Object.keys(this.changes).length === 0) {
          return;
        }

        // Validate: if petFoodType is "mix", ensure ratios are present
        const petFoodType = this.changes.petFoodType !== undefined 
          ? this.changes.petFoodType 
          : this.currentPet.petFoodType;
        const foodType = String(petFoodType || '').toLowerCase().trim();
        
        if (foodType === 'mix') {
          const hasDryRatio = (this.changes.dryFoodRatio !== undefined) || 
                             (this.currentPet.dryFoodRatio !== null && this.currentPet.dryFoodRatio !== undefined);
          const hasWetRatio = (this.changes.wetFoodRatio !== undefined) || 
                             (this.currentPet.wetFoodRatio !== null && this.currentPet.wetFoodRatio !== undefined);
          
          if (!hasDryRatio || !hasWetRatio) {
            alert('Please set both dry and wet food ratios before saving.');
            return;
          }
        }
        
        // Validate date of birth if it's being changed
        if (this.changes.petBirthday !== undefined) {
          const birthdayValue = this.changes.petBirthday;
          if (birthdayValue) {
            const selectedDate = new Date(birthdayValue);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Calculate minimum date: max(1996-01-01, 30 years ago)
            const currentYear = today.getFullYear();
            const thirtyYearsAgo = currentYear - 30;
            const minYear = Math.max(1996, thirtyYearsAgo);
            const minDate = new Date(minYear, 0, 1);
            
            if (isNaN(selectedDate.getTime())) {
              alert('Please enter a valid date of birth.');
              return;
            } else if (selectedDate > today) {
              alert('Date of birth cannot be in the future.');
              return;
            } else if (selectedDate < minDate) {
              alert(`Date of birth must be after ${minYear}-01-01 and not more than 30 years ago.`);
              return;
            }
          }
        }

        this.saving = true;
        const saveBtn = this.querySelector('[data-save-all]');
        if (saveBtn) {
          saveBtn.disabled = true;
          saveBtn.classList.add('loading');
        }

        try {
          if (!window.api || !window.api.petProfile) {
            throw new Error('Gadget API not available');
          }

          const petId = this.currentPet.id;
          
          // Handle image upload if there's a file
          const updateData = { ...this.changes };
          
          // Remove preview URL from update data (only used for display)
          if (updateData.petProfileImagePreview) {
            delete updateData.petProfileImagePreview;
          }
          
          // petProfileImage should already be in StoredFileInput format { fileName, base64 }
          // if it exists in changes

          console.log('Updating pet profile:', petId, updateData);

          const updatedPet = await window.api.petProfile.update(petId, updateData);

          // Finish editing any active field
          if (this.editingFieldKey) {
            this.finishEditingField(this.editingFieldKey, false);
          }

          // Update local pet data from backend response (backend may have cleared/modified fields)
          // This ensures frontend state matches backend state, especially for ratio fields
          Object.assign(this.currentPet, updatedPet);
          
          // Deep copy for original data
          this.originalPetData = JSON.parse(JSON.stringify(this.currentPet));
          this.changes = {};

          // Re-render to show updated values
          this.renderPetData(this.currentPet);
          this.updateFooterVisibility();
          this.updateSaveButton();

          // Dispatch event to update pet card in list
          // Dispatch on document to ensure it reaches the pet-profile-list component
          document.dispatchEvent(new CustomEvent('pet-profile:updated', {
            bubbles: true,
            detail: {
              pet: this.currentPet,
              petId: petId
            }
          }));

          console.log('Pet profile updated successfully:', updatedPet);
        } catch (error) {
          console.error('Error updating pet profile:', error);
          alert('Failed to update pet profile. Please try again.');
        } finally {
          this.saving = false;
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.classList.remove('loading');
          }
        }
      }

      getDeleteConfirmModal() {
        // basic-modal is reparented to document.body while open; only the cached
        // reference from connectedCallback stays valid.
        if (this.deleteConfirmModalEl?.isConnected) {
          return this.deleteConfirmModalEl;
        }
        this.deleteConfirmModalEl = this.querySelector('[data-pet-delete-modal]');
        return this.deleteConfirmModalEl;
      }

      async closeDeleteConfirmModal() {
        const modal = this.getDeleteConfirmModal();
        if (!modal || typeof modal.hide !== 'function') {
          return;
        }
        const done = modal.hide();
        if (done && typeof done.then === 'function') {
          await done;
        }
      }

      /**
       * Replace {pet_name} / {Pet_name} in delete modal copy from theme settings.
       * @param {string} template
       * @returns {string}
       */
      replaceDeleteModalPetPlaceholders(template) {
        if (!template || typeof template !== 'string') return '';
        const raw = (this.currentPet?.petName || '').trim();
        const nameLower = raw || 'this pet';
        const nameSentence = raw || 'This pet';
        return template
          .replace(/\{pet_name\}/g, nameLower)
          .replace(/\{Pet_name\}/g, nameSentence);
      }

      openDeleteConfirmModal() {
        const modal = this.getDeleteConfirmModal();
        if (!modal || !this.currentPet) return;

        const confirmBtn = modal.querySelector('[data-delete-pet-confirm]');
        const cancelBtn = modal.querySelector('[data-delete-pet-cancel]');
        if (confirmBtn) {
          confirmBtn.disabled = false;
          confirmBtn.classList.remove('btn--loading');
        }
        if (cancelBtn) {
          cancelBtn.disabled = false;
        }

        const headingTemplate =
          this.dataset.deleteModalHeadingTemplate || 'Remove {pet_name}?';
        const messageTemplate =
          this.dataset.deleteModalMessageTemplate ||
          '{Pet_name} will be removed from your account. This action is irreversible. Continue?';
        const cancelLabel = this.dataset.deleteModalCancelLabel || 'Cancel';
        const confirmLabel = this.dataset.deleteModalConfirmLabel || 'Remove pet';

        const heading = modal.querySelector('[data-delete-modal-heading]');
        if (heading) {
          heading.textContent = this.replaceDeleteModalPetPlaceholders(headingTemplate);
        }

        const messageEl = modal.querySelector('[data-delete-modal-message]');
        if (messageEl) {
          messageEl.textContent = this.replaceDeleteModalPetPlaceholders(messageTemplate);
        }

        const cancelSpan = cancelBtn?.querySelector('.btn__text');
        if (cancelSpan) {
          cancelSpan.textContent = cancelLabel;
        }
        const confirmSpan = confirmBtn?.querySelector('.btn__text');
        if (confirmSpan) {
          confirmSpan.textContent = confirmLabel;
        }

        const trigger = this.querySelector('[data-delete-pet]');
        modal.show(trigger || null);
      }

      async confirmDeletePet() {
        if (this.deleting || !this.currentPet?.id) return;

        const modal = this.getDeleteConfirmModal();
        const confirmBtn = modal?.querySelector('[data-delete-pet-confirm]');
        const cancelBtn = modal?.querySelector('[data-delete-pet-cancel]');

        this.deleting = true;
        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.classList.add('btn--loading');
        }
        if (cancelBtn) {
          cancelBtn.disabled = true;
        }
        if (modal) {
          modal.setAttribute('aria-busy', 'true');
        }

        try {
          if (!window.api || !window.api.petProfile) {
            throw new Error('Gadget API not available');
          }

          const petId = this.currentPet.id;
          await window.api.petProfile.update(petId, { petDeleted: true });

          await this.closeDeleteConfirmModal();

          Object.assign(this.currentPet, { petDeleted: true });

          document.dispatchEvent(
            new CustomEvent('pet-profile:updated', {
              bubbles: true,
              detail: {
                pet: this.currentPet,
                petId,
              },
            })
          );

          const drawerHide = this.hide();
          if (drawerHide && typeof drawerHide.then === 'function') {
            await drawerHide;
          }
        } catch (error) {
          console.error('Error removing pet profile:', error);
          alert('Could not remove this pet. Please try again.');
        } finally {
          this.deleting = false;
          if (modal) {
            modal.removeAttribute('aria-busy');
          }
          if (confirmBtn) {
            confirmBtn.disabled = false;
            confirmBtn.classList.remove('btn--loading');
          }
          if (cancelBtn) {
            cancelBtn.disabled = false;
          }
        }
      }

      cancelAllChanges() {
        // Finish editing any active field without saving
        if (this.editingFieldKey) {
          this.finishEditingField(this.editingFieldKey, false);
        }

        // Revert to original data
        this.currentPet = JSON.parse(JSON.stringify(this.originalPetData));
        this.changes = {};
        
        // Clear file input
        const fileInput = this.querySelector('[data-image-upload]');
        if (fileInput) {
          fileInput.value = '';
        }
        
        this.renderPetData(this.currentPet);
        this.updateFooterVisibility();
        this.updateSaveButton();
      }


      getAllergyOptions() {
        return [
          'None',
          'Chicken',
          'Duck',
          'Tuna',
          'Salmon',
          'Turkey',
          'Grains',
          'Whitefish',
          'Rice',
          'Oats',
          'Gluten',
          'Lamb',
          'Beef',
          'Dairy',
          'Pork'
        ];
      }

      createAllergyTagCloud(selectedAllergies) {
        const options = this.getAllergyOptions();
        // Ensure selectedAllergies is always a valid array
        let selected = [];
        if (Array.isArray(selectedAllergies)) {
          selected = selectedAllergies
            .map(a => String(a).toLowerCase().trim())
            .filter(a => a && a.length > 0);
        } else if (selectedAllergies !== null && selectedAllergies !== undefined) {
          // If it's not an array, try to convert it
          try {
            const parsed = Array.isArray(selectedAllergies) ? selectedAllergies : [selectedAllergies];
            selected = parsed.map(a => String(a).toLowerCase().trim()).filter(a => a && a.length > 0);
          } catch (e) {
            selected = [];
          }
        }
        
        // Normalize selected values for comparison
        const normalizedSelected = selected.map(a => a.toLowerCase().trim());
        
        // Ensure we always have valid JSON for the hidden input
        const jsonValue = JSON.stringify(selected);
        
        return `
          <div class="pet-profile-drawer__allergy-tag-cloud" data-field-input="petAllergies">
            ${options.map(option => {
              const optionLower = option.toLowerCase().trim();
              const isSelected = normalizedSelected.includes(optionLower);
              return `
                <button 
                  type="button"
                  class="pet-profile-drawer__allergy-tag text-sm-extra ${isSelected ? 'pet-profile-drawer__allergy-tag--selected' : ''}"
                  data-allergy-value="${this.escapeHtml(optionLower)}"
                  aria-pressed="${isSelected}"
                >
                  ${this.escapeHtml(option)}
                </button>
              `;
            }).join('')}
            <input type="hidden" data-allergy-input="petAllergies" value="${this.escapeHtml(jsonValue)}">
          </div>
        `;
      }

      getSelectOptions(fieldKey) {
        // Handle petBreed field - fetch from localStorage, window.petBreeds, or pet-breed-selector
        if (fieldKey === 'petBreed') {
          const petType = this.currentPet?.petType;
          
          if (!petType) {
            console.warn('petType missing, cannot get breeds');
            return [];
          }
          
          const normalizedType = petType.toLowerCase().trim();
          
          // Try localStorage first (most performant)
          if (typeof Storage !== 'undefined') {
            try {
              const cachedData = localStorage.getItem('petBreeds');
              if (cachedData) {
                const parsed = JSON.parse(cachedData);
                if (parsed && Array.isArray(parsed[normalizedType])) {
                  const breeds = parsed[normalizedType];
                  if (breeds.length > 0) {
                    return breeds;
                  }
                }
              }
            } catch (e) {
              console.warn('Error reading pet breeds from localStorage:', e);
            }
          }
          
          // Fallback to window.petBreeds
          if (window.petBreeds && window.petBreeds[normalizedType]) {
            const breeds = window.petBreeds[normalizedType];
            if (Array.isArray(breeds) && breeds.length > 0) {
              return breeds;
            }
          }
          
          // Fallback to petBreedSelector if available
          if (window.petBreedSelector) {
            try {
              const breeds = window.petBreedSelector.getPetBreeds(petType);
              if (Array.isArray(breeds) && breeds.length > 0) {
                return breeds;
              }
            } catch (e) {
              console.warn('Error getting breeds from petBreedSelector:', e);
            }
          }
          
          // No breeds available
          console.warn('No pet breeds available for type:', petType);
          return [];
        }

        // Handle petPooType - fetch from metaobject data
        if (fieldKey === 'petPooType') {
          const pooTypesScript = this.querySelector('[data-poo-types]');
          if (pooTypesScript) {
            try {
              const pooTypes = JSON.parse(pooTypesScript.textContent);
              if (Array.isArray(pooTypes) && pooTypes.length > 0) {
                // Sort by index if available
                pooTypes.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                // Convert to { value, label } format
                return pooTypes.map(item => ({
                  value: item.value || item.name,
                  label: item.name
                }));
              }
            } catch (e) {
              console.error('Error parsing poo types metaobject data:', e);
            }
          }
          // Fallback to empty array if metaobject data not found
          return [];
        }

        // Handle petActivityLevel - fetch from metaobject data (filtered by pet type)
        if (fieldKey === 'petActivityLevel') {
          const activityLevelsScript = this.querySelector('[data-activity-levels]');
          if (activityLevelsScript) {
            try {
              const activityLevelsData = JSON.parse(activityLevelsScript.textContent);
              const petType = this.currentPet?.petType;
              
              if (petType && activityLevelsData[petType.toLowerCase()]) {
                const activityLevels = activityLevelsData[petType.toLowerCase()];
                if (Array.isArray(activityLevels) && activityLevels.length > 0) {
                  // Sort by index if available
                  activityLevels.sort((a, b) => {
                    const indexA = a.index !== undefined ? a.index : 9999;
                    const indexB = b.index !== undefined ? b.index : 9999;
                    return indexA - indexB;
                  });
                  // Convert to { value, label } format
                  return activityLevels.map(item => ({
                    value: item.value || item.name,
                    label: item.name
                  }));
                }
              }
            } catch (e) {
              console.error('Error parsing activity levels metaobject data:', e);
            }
          }
          // Fallback to empty array if metaobject data not found
          return [];
        }

        // Handle petBodyType - fetch from metaobject data (filtered by pet type)
        if (fieldKey === 'petBodyType') {
          const bodyTypesScript = this.querySelector('[data-body-types]');
          if (bodyTypesScript) {
            try {
              const bodyTypesData = JSON.parse(bodyTypesScript.textContent);
              const petType = this.currentPet?.petType;
              
              if (petType && bodyTypesData[petType.toLowerCase()]) {
                const bodyTypes = bodyTypesData[petType.toLowerCase()];
                if (Array.isArray(bodyTypes) && bodyTypes.length > 0) {
                  // Sort by index if available
                  bodyTypes.sort((a, b) => {
                    const indexA = a.index !== undefined ? a.index : 9999;
                    const indexB = b.index !== undefined ? b.index : 9999;
                    return indexA - indexB;
                  });
                  // Convert to { value, label } format
                  return bodyTypes.map(item => ({
                    value: item.value || item.name,
                    label: item.name
                  }));
                }
              }
            } catch (e) {
              console.error('Error parsing body types metaobject data:', e);
            }
          }
          // Fallback to empty array if metaobject data not found
          return [];
        }

        // Handle petEatsTreats - fetch from metaobject data
        if (fieldKey === 'petEatsTreats') {
          const treatsOptionsScript = this.querySelector('[data-treats-options]');
          if (treatsOptionsScript) {
            try {
              const treatsOptions = JSON.parse(treatsOptionsScript.textContent);
              if (Array.isArray(treatsOptions) && treatsOptions.length > 0) {
                // Sort by index if available
                treatsOptions.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                // Convert to { value, label } format
                return treatsOptions.map(item => ({
                  value: item.value || item.name,
                  label: item.name
                }));
              }
            } catch (e) {
              console.error('Error parsing treats options metaobject data:', e);
            }
          }
          // Fallback to empty array if metaobject data not found
          return [];
        }

        // Hardcoded options for other fields
        const options = {
          petType: [
            { value: 'dog', label: 'Dog' },
            { value: 'cat', label: 'Cat' }
          ],
          petGender: [
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' }
          ],
          petFoodType: [
            { value: 'dry', label: 'Dry' },
            { value: 'wet', label: 'Wet' },
            { value: 'mix', label: 'Mix' }
          ]
        };
        return options[fieldKey] || [];
      }

      getCaretIcon() {
        return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="icon" style="position: absolute; top: 50%; transform: translateY(-50%); right: 1.6rem; pointer-events: none;">
          <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      }

      /**
       * Get the display name/label from metaobject data based on the value
       * @param {string} fieldKey - The field key (petPooType, petActivityLevel, petBodyType)
       * @param {string} value - The value to look up
       * @returns {string|null} - The name/label if found, or null
       */
      getMetaobjectLabel(fieldKey, value) {
        if (!value || typeof value !== 'string') {
          return null;
        }

        const normalizedValue = value.toLowerCase().trim();

        // Handle petPooType
        if (fieldKey === 'petPooType') {
          const pooTypesScript = this.querySelector('[data-poo-types]');
          if (pooTypesScript) {
            try {
              const pooTypes = JSON.parse(pooTypesScript.textContent);
              if (Array.isArray(pooTypes)) {
                const found = pooTypes.find(item => {
                  const itemValue = (item.value || item.name || '').toLowerCase().trim();
                  return itemValue === normalizedValue;
                });
                if (found && found.name) {
                  return found.name;
                }
              }
            } catch (e) {
              console.error('Error parsing poo types metaobject data:', e);
            }
          }
        }

        // Handle petActivityLevel - filtered by pet type (use display name, not normalized value)
        if (fieldKey === 'petActivityLevel') {
          const petType = (this.currentPet?.petType || this.currentPet?.pet_type || '').toLowerCase();
          if (petType) {
            let options = null;
            // Try 1: drawer's own [data-activity-levels] (from template)
            const activityLevelsScript = this.querySelector('[data-activity-levels]');
            if (activityLevelsScript) {
              try {
                const data = JSON.parse(activityLevelsScript.textContent);
                options = (data && data[petType] && Array.isArray(data[petType])) ? data[petType] : null;
              } catch (e) { /* ignore */ }
            }
            // Try 2: quiz slide activity-levels-data script (in case drawer template data differs)
            if (!options || options.length === 0) {
              const activitySlide = document.querySelector(`.pet-quiz-slide--${petType}`) || document.querySelector(`.feeding-calculator-slide--${petType}`);
              const slideScript = activitySlide?.querySelector('script[id^="activity-levels-data-"]');
              if (slideScript) {
                try {
                  const parsed = JSON.parse(slideScript.textContent);
                  options = Array.isArray(parsed) ? parsed : null;
                } catch (e) { /* ignore */ }
              }
            }
            // Try 3: global [data-activity-levels] (e.g. quiz-results section)
            if (!options || options.length === 0) {
              const globalScript = document.querySelector('[data-activity-levels]');
              if (globalScript) {
                try {
                  const data = JSON.parse(globalScript.textContent);
                  options = (data && data[petType] && Array.isArray(data[petType])) ? data[petType] : null;
                } catch (e) { /* ignore */ }
              }
            }
            if (options && options.length > 0) {
              const found = options.find(item => {
                const itemValue = (item.value || item.name || '').toLowerCase().trim();
                return itemValue === normalizedValue;
              });
              if (found && found.name) return found.name;
            }
          }
        }

        // Handle petBodyType - filtered by pet type
        if (fieldKey === 'petBodyType') {
          const bodyTypesScript = this.querySelector('[data-body-types]');
          const petType = (this.currentPet?.petType || this.currentPet?.pet_type || '').toLowerCase();
          if (bodyTypesScript && petType) {
            try {
              const bodyTypesData = JSON.parse(bodyTypesScript.textContent);
              const bodyTypes = bodyTypesData[petType];
              if (Array.isArray(bodyTypes)) {
                const found = bodyTypes.find(item => {
                  const itemValue = (item.value || item.name || '').toLowerCase().trim();
                  return itemValue === normalizedValue;
                });
                if (found && found.name) {
                  return found.name;
                }
              }
            } catch (e) {
              console.error('Error parsing body types metaobject data:', e);
            }
          }
        }

        // Handle petEatsTreats - fetch from metaobject data
        if (fieldKey === 'petEatsTreats') {
          const treatsOptionsScript = this.querySelector('[data-treats-options]');
          if (treatsOptionsScript) {
            try {
              const treatsOptions = JSON.parse(treatsOptionsScript.textContent);
              if (Array.isArray(treatsOptions)) {
                const found = treatsOptions.find(item => {
                  const itemValue = (item.value || item.name || '').toLowerCase().trim();
                  return itemValue === normalizedValue;
                });
                if (found && found.name) {
                  return found.name;
                }
              }
            } catch (e) {
              console.error('Error parsing treats options metaobject data:', e);
            }
          }
        }

        return null;
      }

      formatValue(value, key) {
        // Reuse the formatValue logic from buildTableRows
        if (value === null || value === undefined) {
          return '<span class="text-subtext">Not set</span>';
        }

        if (Array.isArray(value)) {
          if (value.length === 0) {
            return '<span class="text-subtext">None</span>';
          }
          return `<ul class="pet-profile-drawer__list">
            ${value.map(item => `<li>${this.escapeHtml(String(item))}</li>`).join('')}
          </ul>`;
        }

        // Handle boolean values (but not petEatsTreats which should be string)
        if (typeof value === 'boolean' && key !== 'petEatsTreats') {
          return value ? '<span class="text-success">Yes</span>' : '<span class="text-subtext">No</span>';
        }

        if (key.includes('Date') || key.includes('At') || key === 'petBirthday') {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              if (key === 'petBirthday') {
                return date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric'
                });
              }
              return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            }
          } catch (e) {
            // Fall through
          }
        }

        if (typeof value === 'number') {
          if (key === 'petWeight') {
            return `${value} kg`;
          }
          // Format ratio fields as percentages
          if (key === 'dryFoodRatio' || key === 'wetFoodRatio') {
            return `${Math.round(value)}%`;
          }
          return String(value);
        }

        if (typeof value === 'string') {
          if (key === 'petType') {
            return this.escapeHtml(value.charAt(0).toUpperCase() + value.slice(1));
          }
          // Format pet food type with ratios when mix
          if (key === 'petFoodType') {
            const foodType = value.toLowerCase().trim();
            let formatted = this.escapeHtml(value.charAt(0).toUpperCase() + value.slice(1));
            if (foodType === 'mix' && this.currentPet) {
              const dryRatio = this.currentPet.dryFoodRatio;
              const wetRatio = this.currentPet.wetFoodRatio;
              if (dryRatio !== null && dryRatio !== undefined && wetRatio !== null && wetRatio !== undefined) {
                formatted += ` (${Math.round(dryRatio)}/${Math.round(wetRatio)})`;
              }
            }
            return formatted;
          }
          // Look up metaobject labels for petPooType, petActivityLevel, petBodyType, petEatsTreats
          if (key === 'petPooType' || key === 'petActivityLevel' || key === 'petBodyType' || key === 'petEatsTreats') {
            const metaobjectLabel = this.getMetaobjectLabel(key, value);
            if (metaobjectLabel) {
              return this.escapeHtml(metaobjectLabel);
            }
            // Fallback to capitalized value if metaobject lookup fails
            return this.escapeHtml(value.split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' '));
          }
          return this.escapeHtml(value);
        }

        return this.escapeHtml(String(value));
      }

      async loadGenderIcon(container, genderType) {
        try {
          // Check if icon is already loaded
          if (container.dataset.loadedIcon === genderType && container.innerHTML.trim() !== '') {
            return;
          }

          // Get the asset URL from data attributes
          const iconUrl = genderType === 'male' 
            ? this.dataset.iconMale 
            : this.dataset.iconFemale;
          
          if (!iconUrl) {
            console.warn(`Gender icon URL not found for: ${genderType}`);
            return;
          }

          // Fetch the SVG file
          const response = await fetch(iconUrl);
          if (!response.ok) {
            console.warn(`Failed to load gender icon: ${iconUrl}`);
            return;
          }

          const svgContent = await response.text();
          
          // Parse and inject the SVG
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
          const svgElement = svgDoc.querySelector('svg');
          
          if (svgElement) {
            // Add icon classes for styling
            svgElement.classList.add('icon', 'icon--large');
            svgElement.setAttribute('aria-hidden', 'true');
            
            // Clear container and add the SVG
            container.innerHTML = '';
            container.appendChild(svgElement);
            container.dataset.loadedIcon = genderType;
          }
        } catch (error) {
          console.error(`Error loading gender icon ${genderType}:`, error);
        }
      }

      calculatePetAge(birthday) {
        if (!birthday) return '';
        
        try {
          const birthDate = new Date(birthday);
          const today = new Date();
          const diffTime = Math.abs(today - birthDate);
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          // Calculate years and months
          const years = Math.floor(diffDays / 365);
          const months = Math.floor((diffDays % 365) / 30);
          
          if (years > 0) {
            return months > 0 
              ? `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}` 
              : `${years} year${years !== 1 ? 's' : ''}`;
          } else if (months > 0) {
            return `${months} month${months !== 1 ? 's' : ''}`;
          } else {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
          }
        } catch (error) {
          console.error('Error calculating pet age:', error);
          return '';
        }
      }

      validateDateField(field) {
        if (!field || field.type !== 'date' || !field.hasAttribute('data-field-input') || field.getAttribute('data-field-input') !== 'petBirthday') return;
        
        const dateValue = field.value.trim();
        const row = field.closest('tr');
        if (!row) return;
        
        const existingError = row.querySelector('.pet-profile-drawer__date-error');
        
        // Remove existing error first
        if (existingError) {
          existingError.remove();
        }
        field.classList.remove('error');
        field.removeAttribute('aria-invalid');
        
        // Validate date range
        if (dateValue) {
          const selectedDate = new Date(dateValue);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
          
          // Calculate minimum date: max(1996-01-01, 30 years ago)
          const currentYear = today.getFullYear();
          const thirtyYearsAgo = currentYear - 30;
          const minYear = Math.max(1996, thirtyYearsAgo);
          const minDate = new Date(minYear, 0, 1); // January 1st of minYear
          
          let isValid = true;
          let errorMessage = '';
          
          // Check if date is valid
          if (isNaN(selectedDate.getTime())) {
            isValid = false;
            errorMessage = 'Please enter a valid date';
          } 
          // Check if date is in the future
          else if (selectedDate > today) {
            isValid = false;
            errorMessage = 'Date cannot be in the future';
          } 
          // Check if date is older than 30 years (or before 1996)
          else if (selectedDate < minDate) {
            isValid = false;
            errorMessage = `Date must be after ${minYear}-01-01 and not more than 30 years ago`;
          }
          
          if (!isValid) {
            field.classList.add('error');
            field.setAttribute('aria-invalid', 'true');
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'pet-profile-drawer__date-error pet-quiz-slide__error-message';
            errorDiv.textContent = errorMessage;
            
            const valueCell = row.querySelector('.pet-profile-drawer__field-value');
            if (valueCell) {
              valueCell.appendChild(errorDiv);
            } else {
              row.appendChild(errorDiv);
            }
          }
        }
      }

      escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}

